import React, { useState, useEffect, useRef } from "react";
import { navigate } from "@reach/router";
import "./css/summary_sheet.scss";

import StandardTemplate from "../../templates/standard_template/standard_template";
import CompanyClientInfo from "../../components/loan_tools/summary_sheet/company_client_info";
import GeneralInfo from "../../components/loan_tools/summary_sheet/general_info";
import LoanSummaries from "../../components/loan_tools/summary_sheet/loan_summaries";
import CalculatingCashToClose from "../../components/loan_tools/summary_sheet/calculating_cash_to_close";
import Comparisons from "../../components/loan_tools/summary_sheet/comparisons";
import LoanSelection from "../../components/loan_tools/summary_sheet/loan_selection";
import Trid from "./trid";
import TotalCostAnalysis from "../../components/loan_tools/summary_sheet/total_cost_analysis";
import RefinanceTcaGraphs from "../../components/loan_tools/summary_sheet/refinance_tca_graphs";

import { useDocument } from "../../components/loan_tools/loan_tool";
import { useUser } from "../../hooks/user";
import { loan_visible_for_viewer } from "../../constants/lender_visibility";
import NoLoans from "./no_loans";
import NoBorrowers from "./no_borrowers";
import { PURCHASE, REFINANCE } from "../../constants/document_types";
import RefinanceGeneralInfoLoanSummary from "../../components/loan_tools/summary_sheet/refinance_general_info_loan_summary";
import RefinanceCalculatingCashToClose from "../../components/loan_tools/summary_sheet/refinance_calculating_cash_to_close";

/**
 * Purchase Summary Sheet
 */
export default function PurchaseSummarySheet() {
  const [
    document,
    setDocument,
    { documentType, documentId, documentAccountProfile },
    { saveDocument, loadDocument }
  ] = useDocument();
  const [{ accountProfile, companyProfile }] = useUser();
  const [activePage, setActivePage] = useState("PURCHASE_SUMMARY");
  // When set, print the given document (with a nice PDF filename) once its tab
  // has rendered. Lets a "Save PDF" button jump to the right view and save it.
  const [pendingSave, setPendingSave] = useState(null);

  // Pre-Qual / Pre-Approval letter flow (Purchase only): the Forms menu sends the
  // user here with ?openLetter=<key>. When the user picks a loan number below we
  // read that flag from the URL, save the document, and open the letter in a new
  // tab. letterOpenRef holds the blank tab opened during the click (so the popup
  // blocker allows it) plus which letter to open; the effect below redirects it
  // once the selection has been saved. letterOpenTick fires that effect.
  const letterOpenRef = useRef(null);
  const [letterOpenTick, setLetterOpenTick] = useState(0);

  const isPurchase = documentType === PURCHASE;
  const isRefinance = documentType === REFINANCE;

  async function switchTab(activePage) {
    await saveDocument();
    setActivePage(activePage);
  }

  const borrowerLast =
    (document.applicant &&
      document.applicant.borrowers &&
      document.applicant.borrowers[0] &&
      document.applicant.borrowers[0].last_name) ||
    "Borrower";

  // Print the current view with a chosen filename. Browsers use document.title as
  // the default "Save as PDF" filename, so each saved doc drops into the
  // customer's folder cleanly named. Restored shortly after.
  function printWithTitle(title) {
    const prev = window.document.title;
    window.document.title = title;
    window.print();
    setTimeout(() => {
      window.document.title = prev;
    }, 1500);
  }

  // Save one document as its own PDF: jump to its tab if needed, then print it
  // once rendered (see the effect below). Already on the tab -> print now.
  function savePdf(tab, title) {
    if (activePage === tab) {
      printWithTitle(title);
    } else {
      setPendingSave({ tab, title });
      switchTab(tab);
    }
  }

  // Open the LO's own email client (Outlook/Gmail) pre-filled with the
  // borrower(s), subject, and a short message + signature. mailto can't carry an
  // attachment, so the LO attaches the PDF(s) they saved - same as today, minus
  // the typing. No backend, no deliverability/compliance exposure.
  function emailDocs() {
    const borrowers =
      (document.applicant && document.applicant.borrowers) || [];
    const to = borrowers
      .map(b => b && b.email)
      .filter(Boolean)
      .join(",");
    const lo = [
      documentAccountProfile.first_name,
      documentAccountProfile.last_name
    ]
      .filter(Boolean)
      .join(" ");
    const subject = `${borrowerLast} — Loan Summary${lo ? " from " + lo : ""}`;
    // NOTE: no body. A mailto with a body suppresses Outlook's/Gmail's auto
    // signature, so we pre-fill only the recipient(s) and subject and let the
    // LO's own standard signature appear. (They type any note + attach the PDFs.)
    window.location.href =
      "mailto:" +
      encodeURIComponent(to) +
      "?subject=" +
      encodeURIComponent(subject);
  }

  function updateSummary(updatedSummary) {
    const summary = {
      ...document.summary,
      ...updatedSummary
    };
    setDocument({ ...document, summary });

    // If the user just chose a loan number AND we arrived from the Forms-menu
    // dialog (?openLetter=<key> is still in the URL), flag it so the effect below
    // saves the selection, then opens the letter IN THE SAME TAB (in-app), keeping
    // one shared document context.
    if (
      Object.prototype.hasOwnProperty.call(updatedSummary, "selection_loan_number") &&
      updatedSummary.selection_loan_number
    ) {
      const letter = new URLSearchParams(window.location.search).get("openLetter");
      if (letter === "pre-qual-letter" || letter === "pre-approval-letter") {
        letterOpenRef.current = { letter };
        setLetterOpenTick(tick => tick + 1);
      }
    }
  }

  function updateLoan(updatedLoan, loanIndex) {
    const loans = [...document.loans];
    loans[loanIndex] = {
      ...loans[loanIndex],
      ...updatedLoan
    };
    setDocument({ ...document, loans });
  }

  useEffect(() => {
    loadDocument(documentId);
  }, []);

  // Once the requested document's tab is showing, print it (small delay so the
  // heavy tables finish laying out), then clear the request.
  useEffect(() => {
    if (!pendingSave || activePage !== pendingSave.tab) return;
    const { title } = pendingSave;
    setPendingSave(null);
    const timer = setTimeout(() => printWithTitle(title), 450);
    return () => clearTimeout(timer);
  }, [activePage, pendingSave]);

  // Auto-scroll to the "I / We select Loan Number" field when arriving from the
  // Forms-menu dialog, so it's right in front of the loan officer.
  // (`document` here is the loan doc from useDocument — use window.document for DOM.)
  useEffect(() => {
    const letter = new URLSearchParams(window.location.search).get("openLetter");
    if (!letter) return;
    const timer = setTimeout(() => {
      const el = window.document.querySelector(".loan-selection");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 500);
    return () => clearTimeout(timer);
  }, [document.loans && document.loans.length]);

  // After the loan selection commits to state, save it then open the letter in the
  // SAME tab (in-app navigation) so the letter reads the up-to-date document and
  // shares one document context. The letter's "Back" button returns here.
  useEffect(() => {
    const pending = letterOpenRef.current;
    if (!pending) return;
    if (!document.summary || !document.summary.selection_loan_number) return;
    letterOpenRef.current = null;
    (async () => {
      await saveDocument();
      navigate(`/loan-tool/${documentId}/forms/${pending.letter}`);
    })();
  }, [letterOpenTick]);

  return (
    <StandardTemplate className="summary-standard-template">
      <NoBorrowers />
      <NoLoans />

      {document.loans.length > 0 && document.applicant.borrowers.length > 0 && (
        <div className="summary-container">
          <div className="summary-tab-container">
            <div
              key="-1"
              className={`summary-tab ${activePage === "PURCHASE_SUMMARY" &&
                "active"}`}
              onClick={() => switchTab("PURCHASE_SUMMARY")}
            >
              {isPurchase ? "Purchase" : "Refinance"} Summary
            </div>
            {isPurchase && (
              <div
                key="0"
                className={`summary-tab ${activePage ===
                  "TOTAL_COST_ANALYSIS" && "active"}`}
                onClick={() => switchTab("TOTAL_COST_ANALYSIS")}
              >
                Total Cost Analysis
              </div>
            )}
            {isRefinance && (
              <div
                key="0r"
                className={`summary-tab ${activePage ===
                  "REFINANCE_TCA" && "active"}`}
                onClick={() => switchTab("REFINANCE_TCA")}
              >
                Total Cost Analysis
              </div>
            )}
            {document.loans.map((loan, index) => {
              if (!loan_visible_for_viewer(loan, accountProfile)) {
                return null;
              }
              const tab = "LOAN_" + (index + 1);
              return (
                <div
                  key={index + 1}
                  className={`summary-tab ${activePage === tab && "active"}`}
                  onClick={() => switchTab(tab)}
                >
                  Loan {index + 1} TRID
                </div>
              );
            })}
            <button
              className="summary-print-button no-print"
              onClick={() => window.print()}
            >
              Print
            </button>
          </div>

          <div className="summary-pdf-toolbar no-print">
            <span className="summary-pdf-label">Save as PDF:</span>
            <button
              className="button"
              onClick={() =>
                savePdf("PURCHASE_SUMMARY", `${borrowerLast} Summary Sheet`)
              }
            >
              Summary
            </button>
            <button
              className="button"
              onClick={() =>
                savePdf(
                  isPurchase ? "TOTAL_COST_ANALYSIS" : "REFINANCE_TCA",
                  `${borrowerLast} Total Cost Analysis`
                )
              }
            >
              Total Cost Analysis
            </button>
            {document.loans.map(
              (loan, index) =>
                loan_visible_for_viewer(loan, accountProfile) && (
                  <button
                    key={index}
                    className="button"
                    onClick={() =>
                      savePdf(
                        "LOAN_" + (index + 1),
                        `${borrowerLast} Loan ${index + 1} LE`
                      )
                    }
                  >
                    {`Loan ${index + 1} LE`}
                  </button>
                )
            )}
            <button
              className="button summary-email-button"
              onClick={emailDocs}
              title="Open your email with the borrower and a message pre-filled, then attach the PDF(s) you saved"
            >
              Email…
            </button>
          </div>
          {activePage === "PURCHASE_SUMMARY" && (
            <div className="summary-sheet">
              <table className="summary-sheet-main-table">
                <colgroup>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <CompanyClientInfo />
                {isPurchase && (
                  <>
                    <GeneralInfo />
                    <LoanSummaries updateSummary={updateSummary} />
                    <CalculatingCashToClose />
                  </>
                )}
                {isRefinance && (
                  <>
                    <RefinanceGeneralInfoLoanSummary
                      updateSummary={updateSummary}
                    />
                    <RefinanceCalculatingCashToClose
                      updateSummary={updateSummary}
                    />
                  </>
                )}
                <Comparisons />
                <LoanSelection updateSummary={updateSummary} />
              </table>
            </div>
          )}
          {isPurchase && activePage === "TOTAL_COST_ANALYSIS" && (
            <TotalCostAnalysis />
          )}
          {isRefinance && activePage === "REFINANCE_TCA" && (
            <RefinanceTcaGraphs updateSummary={updateSummary} />
          )}
          {document.loans.map(
            (loan, index) =>
              loan_visible_for_viewer(loan, accountProfile) &&
              activePage === "LOAN_" + (index + 1) && (
                <Trid
                  key={index}
                  updateLoan={updatedLoan => updateLoan(updatedLoan, 0)}
                  loan={loan}
                  loanNumber={index + 1}
                />
              )
          )}
        </div>
      )}
    </StandardTemplate>
  );
}
